import type { ReactNode } from 'react';

import {
  ConfirmModalProvider,
  useConfirmModal,
  type ConfirmModalCustomer,
  type PriceChangedContent,
} from '@lib/context/confirm-modal-context';
import ConfirmModal, { type ConfirmModalTab } from './confirm-modal';
import DepositConfirm from './deposit-confirm';
import MonthlySettlementConfirm from './monthly-settlement-confirm';
import PriceChangedConfirm from './price-changed-confirm';

// =============================================================================
// Layout Confirm Modal Provider (combines Context + Modal)
// =============================================================================

interface LayoutConfirmModalProviderProps {
  children: ReactNode;
  customer: ConfirmModalCustomer | null;
  isActive: boolean;
  priceChangedContent: PriceChangedContent | null;
}

export function LayoutConfirmModalProvider({
  children,
  customer,
  isActive,
  priceChangedContent,
}: LayoutConfirmModalProviderProps) {
  return (
    <ConfirmModalProvider
      customer={customer}
      isActive={isActive}
      priceChangedContent={priceChangedContent}
    >
      {children}
      {!!customer && isActive && (
        <LayoutConfirmModalRenderer />
      )}
    </ConfirmModalProvider>
  );
}

// =============================================================================
// Layout Confirm Modal Renderer (internal component)
// =============================================================================

function LayoutConfirmModalRenderer() {
  const {
    isModalOpen,
    openModals,
    closeModal,
    skipPaymentConfirm,
    confirmPayment,
    confirmPriceChanged,
    isMonthlySettlement,
    priceChangedContent,
  } = useConfirmModal();

  // Build tabs array based on which confirmations are needed
  const tabs: ConfirmModalTab[] = [];

  if (openModals.payment_confirm) {
    tabs.push({
      title: isMonthlySettlement ? '應結清貨款確認' : '儲值金確認',
      content: isMonthlySettlement ? (
        <MonthlySettlementConfirm />
      ) : (
        <DepositConfirm />
      ),
      onSkip: skipPaymentConfirm,
      onConfirm: confirmPayment,
      testIdPrefix: isMonthlySettlement
        ? 'confirm-modal-monthly-settlement'
        : 'confirm-modal-deposit',
    });
  }

  if (openModals.price_changed_confirm) {
    tabs.push({
      title: '價格異動',
      content: <PriceChangedConfirm priceChangedContent={priceChangedContent} />,
      onConfirm: confirmPriceChanged,
      testIdPrefix: 'confirm-modal-price-changed',
    });
  }

  return (
    <ConfirmModal
      isModalOpen={isModalOpen}
      onClose={closeModal}
      tabs={tabs}
    />
  );
}

// Re-export context hook and types
export { useConfirmModal } from '@lib/context/confirm-modal-context';
export type { ConfirmModalContextValue } from '@lib/context/confirm-modal-context';
